import React, { useState } from 'react';
import { useFitnessContext } from '../context/FitnessContext';
import { useThemeContext } from '../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Label from '../components/ui/Label';
import { FiDroplet, FiCalendar, FiPlus, FiCheck, FiTarget, FiMinus, FiTrendingUp, FiChevronLeft, FiChevronRight, FiEdit2 } from 'react-icons/fi';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const Hydration = () => {
  const { 
    waterGoal, 
    setWaterGoal, 
    getIntakeForDate, 
    addWater, 
    removeWater, 
    resetIntakeForDate,
    getWeeklyData,
    getMonthlyTrend,
    getStats,
    getLocalDateString 
  } = useFitnessContext();
  
  const { theme } = useThemeContext();
  const isDark = theme === 'dark';

  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [customAmount, setCustomAmount] = useState('8');
  const [isEditingGoal, setIsEditingGoal] = useState(false);
  const [tempGoal, setTempGoal] = useState(waterGoal.toString());

  const todayIntake = getIntakeForDate(selectedDate);
  const weeklyWaterIntake = getWeeklyData();
  const monthlyWaterTrend = getMonthlyTrend();
  const stats = getStats();

  // Added 33.8 (1 Liter) to quick add amounts
  const quickAddAmounts = [8, 12, 16, 20, 33.8];

  const waterPercentage = (todayIntake / waterGoal) * 100;
  const goalCompletionRate = stats.daysTracked > 0 
    ? Math.round((stats.goalsReached / stats.daysTracked) * 100) 
    : 0;
  
  const isToday = selectedDate === getLocalDateString();

  const handleDateChange = (days) => {
    const date = new Date(selectedDate + 'T12:00:00'); // Use noon to avoid TZ issues when adding days
    date.setDate(date.getDate() + days);
    setSelectedDate(getLocalDateString(date));
  };

  const handleSaveGoal = () => {
    const newGoal = parseInt(tempGoal);
    if (!isNaN(newGoal) && newGoal > 0) {
      setWaterGoal(newGoal);
      setIsEditingGoal(false);
    }
  };

  const chartAxisColor = isDark ? '#94a3b8' : '#64748b';
  const chartGridColor = isDark ? '#334155' : '#e2e8f0';
  const tooltipStyle = {
    backgroundColor: isDark ? '#1e293b' : '#ffffff',
    borderColor: isDark ? '#334155' : '#e2e8f0',
    color: isDark ? '#f8fafc' : '#0f172a'
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">Hydration Tracker</h1>
          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400">
            Stay hydrated and optimize your training performance
          </p>
        </div>
        <div className="flex items-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-1 shadow-sm">
          <Button variant="ghost" size="sm" onClick={() => handleDateChange(-1)}>
            <FiChevronLeft className="w-4 h-4" />
          </Button>
          <div className="px-4 text-center min-w-[140px]">
            <p className="text-sm font-bold text-slate-900 dark:text-white">
              {isToday ? 'Today' : new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
            <input 
              type="date" 
              value={selectedDate} 
              onChange={(e) => setSelectedDate(e.target.value)}
              className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider bg-transparent border-none p-0 h-auto focus:ring-0 cursor-pointer"
            />
          </div>
          <Button variant="ghost" size="sm" onClick={() => handleDateChange(1)} disabled={isToday}>
            <FiChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Key Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Avg Daily</p>
              <FiDroplet className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-600 dark:text-cyan-400" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
              {stats.avgWaterIntake} oz
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">per day</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Days Tracked</p>
              <FiCalendar className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">{stats.daysTracked}</p>
            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
              {stats.daysTracked > 0 ? 'Keep it up!' : 'Start tracking!'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Goals Reached</p>
              <FiTarget className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">{stats.goalsReached}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{goalCompletionRate}% success</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Current Streak</p>
              <FiTrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">{stats.currentStreak}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">days in a row</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
        {/* Date-Specific Water Intake Tracker */}
        <Card className={isToday ? "border-cyan-200 dark:border-cyan-900/50" : "border-slate-200 dark:border-slate-700"}>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg flex items-center justify-between">
              <div className="flex items-center">
                <FiDroplet className="w-5 h-5 mr-2 text-cyan-600 dark:text-cyan-400" />
                Intake for {isToday ? "Today" : new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </div>
              {!isToday && (
                <Button variant="ghost" size="sm" onClick={() => setSelectedDate(getLocalDateString())} className="text-xs text-blue-600 dark:text-blue-400">
                  Back to Today
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Progress Display */}
              <div className="text-center py-4">
                <div className="inline-flex items-center space-x-2">
                  <span className="text-4xl sm:text-5xl font-bold text-cyan-600 dark:text-cyan-400">
                    {todayIntake}
                  </span>
                  <span className="text-xl sm:text-2xl text-slate-600 dark:text-slate-400">oz</span>
                  <span className="text-slate-400 dark:text-slate-500 mx-2">/</span>
                  {isEditingGoal ? (
                    <div className="flex items-center gap-2">
                      <Input 
                        type="number" 
                        value={tempGoal} 
                        onChange={(e) => setTempGoal(e.target.value)}
                        className="w-20 h-10 text-xl font-bold text-slate-900 dark:text-white"
                        autoFocus
                      />
                      <Button size="sm" onClick={handleSaveGoal}>Save</Button>
                      <Button variant="ghost" size="sm" onClick={() => setIsEditingGoal(false)}>Cancel</Button>
                    </div>
                  ) : (
                    <div className="flex items-center group cursor-pointer" onClick={() => { setTempGoal(waterGoal.toString()); setIsEditingGoal(true); }}>
                      <span className="text-xl sm:text-2xl text-slate-600 dark:text-slate-400">{waterGoal} oz</span>
                      <FiEdit2 className="w-3 h-3 ml-2 text-slate-400 dark:text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  )}
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                  {Math.round(waterPercentage)}% of daily goal
                </p>
              </div>
              
              {/* Progress Bar */}
              <div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-6 overflow-hidden">
                  <div 
                    className="bg-gradient-to-r from-cyan-400 to-blue-500 dark:from-cyan-500 dark:to-blue-600 h-6 rounded-full transition-all duration-300 flex items-center justify-center"
                    style={{ width: `${Math.min(waterPercentage, 100)}%` }}
                  >
                    {waterPercentage >= 15 && (
                      <span className="text-xs font-medium text-white">
                        {Math.round(waterPercentage)}%
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Add Buttons */}
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Quick Add</p>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {quickAddAmounts.map((amount) => (
                    <Button 
                      key={amount} 
                      onClick={() => addWater(amount, selectedDate)}
                      variant="outline"
                      size="sm"
                      className="flex flex-col items-center py-3"
                    >
                      <FiPlus className="w-4 h-4 mb-1" />
                      <span className="text-xs">{amount} oz</span>
                    </Button>
                  ))}
                </div>
              </div>

              {/* Custom Amount */}
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Custom Amount</p>
                <div className="flex gap-2">
                  <Input 
                    type="number" 
                    placeholder="8" 
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    className="flex-1"
                    min="1"
                    max="100"
                  />
                  <Button 
                    onClick={() => addWater(customAmount, selectedDate)}
                    disabled={!customAmount || parseInt(customAmount) <= 0}
                    className="flex-shrink-0"
                  >
                    <FiPlus className="w-4 h-4 mr-1" /> Add
                  </Button>
                  <Button 
                    onClick={() => removeWater(customAmount, selectedDate)}
                    disabled={!customAmount || parseInt(customAmount) <= 0 || todayIntake === 0}
                    variant="outline"
                    className="flex-shrink-0"
                  >
                    <FiMinus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Reset Button */}
              <Button 
                onClick={() => resetIntakeForDate(selectedDate)}
                disabled={todayIntake === 0}
                variant="ghost"
                size="sm"
                className="w-full text-slate-600 dark:text-slate-400"
              >
                Reset Intake for this day
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Hydration Insights */}
        <Card className="border-blue-200 dark:border-blue-900/50">
          <CardHeader>
            <CardTitle className="text-base sm:text-lg flex items-center">
              <FiTarget className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
              Hydration Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Goal Completion Rate</p>
                  <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">{goalCompletionRate}%</span>
                </div>
                <div className="w-full bg-blue-200 dark:bg-blue-900/50 rounded-full h-2 overflow-hidden">
                  <div className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full transition-all duration-300" style={{ width: `${goalCompletionRate}%` }} />
                </div>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">
                  {stats.goalsReached} out of {stats.daysTracked} days
                </p>
              </div>

              <div className="bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-cyan-900 dark:text-cyan-100">7-Day Average</p>
                  <FiDroplet className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                </div>
                <p className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">{stats.avgWaterIntake} oz</p>
                <p className="text-xs text-cyan-700 dark:text-cyan-300 mt-1">
                  {stats.avgWaterIntake >= waterGoal ? 'Above' : 'Below'} daily goal
                </p>
              </div>

              <div className={`rounded-lg p-4 ${
                todayIntake >= waterGoal 
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50' 
                  : todayIntake >= waterGoal * 0.5 
                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50' 
                    : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50'
              }`}>
                <p className={`text-sm font-medium mb-1 ${
                  todayIntake >= waterGoal ? 'text-green-900 dark:text-green-100' :
                  todayIntake >= waterGoal * 0.5 ? 'text-yellow-900 dark:text-yellow-100' :
                  'text-red-900 dark:text-red-100'
                }`}>
                  {todayIntake >= waterGoal ? '✅ Well Hydrated' : todayIntake >= waterGoal * 0.5 ? '⚠️ Moderate Hydration' : '❌ Need More Water'}
                </p>
                <p className={`text-xs ${
                  todayIntake >= waterGoal ? 'text-green-700 dark:text-green-300' :
                  todayIntake >= waterGoal * 0.5 ? 'text-yellow-700 dark:text-yellow-300' :
                  'text-red-700 dark:text-red-300'
                }`}>
                  {todayIntake >= waterGoal 
                    ? "Properly hydrated for optimal performance!" 
                    : `Need ${Math.max(0, waterGoal - todayIntake)} more oz to reach the goal.`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg flex items-center">
              <FiDroplet className="w-5 h-5 mr-2 text-cyan-600 dark:text-cyan-400" />
              Weekly Water Intake
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={weeklyWaterIntake}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                <XAxis dataKey="day" tick={{ fontSize: 12, fill: chartAxisColor }} />
                <YAxis domain={[0, Math.max(waterGoal + 20, 100)]} tick={{ fontSize: 12, fill: chartAxisColor }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="ounces" fill={isDark ? "#0891b2" : "#06b6d4"} name="Ounces" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg flex items-center">
              <FiTrendingUp className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
              Monthly Average Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={monthlyWaterTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                <XAxis dataKey="week" tick={{ fontSize: 12, fill: chartAxisColor }} />
                <YAxis domain={[0, Math.max(waterGoal + 20, 100)]} tick={{ fontSize: 12, fill: chartAxisColor }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line type="monotone" dataKey="avgOunces" stroke={isDark ? "#3b82f6" : "#2563eb"} strokeWidth={2} name="Avg Ounces" dot={{ fill: isDark ? '#3b82f6' : '#2563eb', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Hydration;